import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { useApp, AppProvider } from './context/AppContext';
import axios from 'axios';
import {
  Menu, X, Instagram, Linkedin, Twitter,
  ArrowRight, User, ShoppingBag, CheckCircle,
  Heart, BookOpen, Quote, Award, Mail, Phone, ChevronRight, Maximize2, Shield, Sparkles, Zap, ExternalLink, Send, Key
} from 'lucide-react';

// ASSETS Configuration
const ASSETS = {
  AUTHOR: '/assets/images/author.jpg',
  LOGO: 'MB',
  BOOKS: {
    HINDI: '/assets/images/hindi_cover_full.jpeg',
    GUJARATI: '/assets/images/gujarati_cover_full.jpeg',
    ENGLISH: '/assets/images/english_cover_full.jpeg',
  }
};

const SHARED_CONTENT = {
  MANUAL: "Manual to Understand Nuisance of Human Mind",
  MEMOIR_TEXT: "This isn't just a memoir; it's personal record of an IAS to navigating the darkest corridors of the mind. By merging medical knowledge with lived spiritual experience, Dr. Manish Bansal provides a logical, step-by-step path toward light, very bright light.",
  CONFESSION_TAG: "MY DEPRESSION DIARIES- Confessions of an IAS who failed fell miserably but rose miraculously meticulously"
};

const BOOKS_DATA = [
  { id: 'hindi', title: 'मेरी डिप्रेशन डायरीज', subtitle: SHARED_CONTENT.MANUAL, lang: 'Hindi', description: SHARED_CONTENT.MEMOIR_TEXT, features: [SHARED_CONTENT.CONFESSION_TAG, 'वैज्ञानिक और आध्यात्मिक'], priceHard: '360/-', mrp: '400/-', coverImage: ASSETS.BOOKS.HINDI },
  { id: 'gujarati', title: 'મારી ડિપ્રેશન ડાયરીઝ', subtitle: SHARED_CONTENT.MANUAL, lang: 'Gujarati', description: SHARED_CONTENT.MEMOIR_TEXT, features: [SHARED_CONTENT.CONFESSION_TAG, 'વૈજ્ઞાનિક અને આધ્યાત્મિક'], priceHard: '360/-', mrp: '400/-', coverImage: ASSETS.BOOKS.GUJARATI },
  { id: 'english', title: 'My Depression Diaries', subtitle: SHARED_CONTENT.MANUAL, lang: 'English', description: SHARED_CONTENT.MEMOIR_TEXT, features: [SHARED_CONTENT.CONFESSION_TAG, 'Science & Spirituality'], priceHard: '360/-', mrp: '400/-', coverImage: ASSETS.BOOKS.ENGLISH }
];

const fadeInUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.8, ease: "easeOut" } };

const FullCoverModal = ({ imageUrl, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4 md:p-12 text-center" onClick={onClose}>
    <button className="absolute top-4 right-4 md:top-8 md:right-8 text-white hover:text-[#C5A059] transition-all">
      <X size={32} className="md:w-12 md:h-12" />
    </button>
    <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="max-w-7xl w-full bg-[#1A1A1A] p-2 shadow-2xl border border-white/10" onClick={e => e.stopPropagation()}>
      <img src={imageUrl} alt="Full Book Cover" className="w-full h-auto shadow-inner" />
      <p className="sans text-white/40 text-center mt-4 text-[10px] md:text-xs tracking-widest uppercase font-bold">Full Spread View: Back • Spine • Front</p>
    </motion.div>
  </motion.div>
);

const Navbar = () => {
  const { user, logout } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{ name: 'HOME', href: '/' }, { name: 'ABOUT', href: '/#about' }, { name: 'BOOKS', href: '/book' }, { name: 'ORDERS', href: '/orders' }];
  return (
    <nav className={`fixed w-full z-[100] transition-all duration-500 ${isScrolled ? 'bg-white py-3 shadow-lg border-b border-[#7A1B1B]/10' : 'bg-transparent py-6 md:py-8'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-12 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 md:gap-3 shrink-1 min-w-0">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-[#7A1B1B] flex items-center justify-center rounded-full shadow-lg shrink-0"> <span className="serif text-white text-lg md:text-xl font-bold uppercase">MB</span> </div>
          <div className="flex flex-col min-w-0 overflow-hidden text-left">
            <span className="serif text-[14px] sm:text-lg md:text-xl lg:text-2xl font-bold tracking-tight text-[#7A1B1B] truncate uppercase leading-tight"> DR. MANISH KUMAR <span className="text-[#C5A059]">BANSAL</span> </span>
            <span className="sans text-[7px] md:text-[9px] tracking-[0.2em] md:tracking-[0.4em] uppercase text-[#7A1B1B] font-black opacity-80 leading-tight">IAS • Author</span>
          </div>
        </Link>
        <div className="hidden lg:flex items-center space-x-6 xl:space-x-12">
          {navLinks.map((link) => (<Link key={link.name} to={link.href} className="nav-link font-bold text-[#2D2D2D] hover:text-[#7A1B1B] transition-colors text-xs xl:text-sm uppercase tracking-widest">{link.name}</Link>))}
          <div className="h-6 w-[1px] bg-[#7A1B1B]/20" />
          {user ? (
            <div className="flex items-center gap-6">
              <span className="sans text-[11px] font-black uppercase text-[#7A1B1B] tracking-widest truncate max-w-[100px]">{user.fullname || user.username}</span>
              <button onClick={logout} className="text-[#7A1B1B] hover:text-red-600 transition-colors"><X size={20} /></button>
            </div>
          ) : (<Link to="/login" className="btn-maroon !py-3 !px-8 text-[11px] uppercase tracking-widest font-black">LOGIN</Link>)}
        </div>
        <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden text-[#7A1B1B] p-2"> {isOpen ? <X size={28} /> : <Menu size={28} />} </button>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white border-b border-[#7A1B1B]/10 overflow-hidden shadow-2xl" >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (<Link key={link.name} to={link.href} onClick={() => setIsOpen(false)} className="sans text-sm font-black tracking-[0.2em] text-[#2D2D2D] hover:text-[#7A1B1B] uppercase" > {link.name} </Link>))}
              <div className="h-[1px] bg-gray-100 w-full" />
              {user ? (
                <div className="flex justify-between items-center">
                  <span className="sans text-xs font-black uppercase text-[#7A1B1B]">{user.fullname || user.username}</span>
                  <button onClick={logout} className="text-red-600 font-bold text-xs uppercase">Logout</button>
                </div>
              ) : (<Link to="/login" onClick={() => setIsOpen(false)} className="btn-maroon text-center py-4 text-xs font-bold tracking-[0.2em] uppercase">LOGIN</Link>)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const BookEdition = ({ book, isReversed }) => {
  const [showBack, setShowBack] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className={`grid lg:grid-cols-12 gap-10 lg:gap-20 items-center ${isReversed ? 'lg:flex-row-reverse' : ''} text-left`}>
      <motion.div {...fadeInUp} className={`lg:col-span-5 ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
        <div className="book-container group relative max-w-[280px] sm:max-w-sm mx-auto lg:max-w-none">
          <div className={`book-card aspect-[3/4] cursor-pointer shadow-2xl ${showBack ? 'flipped' : ''}`} onClick={() => setShowBack(!showBack)}>
            <div className="book-face border-2 md:border-4 border-white overflow-hidden bg-zinc-900 relative">
              <img src={book.coverImage} alt="Front" className="absolute h-full w-auto max-w-none right-0" />
              <div className="absolute top-4 left-4 bg-[#7A1B1B] text-white px-3 py-1 sans text-[8px] font-black uppercase tracking-widest z-10 shadow-lg uppercase text-[10px]">Front Cover</div>
            </div>
            <div className="book-face book-face-back border-2 md:border-4 border-white overflow-hidden bg-zinc-900 relative">
              <img src={book.coverImage} alt="Back" className="absolute h-full w-auto max-w-none left-0" />
              <div className="absolute top-4 left-4 bg-[#C5A059] text-white px-3 py-1 sans text-[8px] font-black uppercase tracking-widest z-10 shadow-lg uppercase text-[10px]">Back Cover</div>
            </div>
          </div>
          <div className="mt-6 flex flex-col items-center gap-3 text-center">
            <button onClick={() => setShowBack(!showBack)} className="sans text-[10px] font-black tracking-[0.2em] uppercase text-[#7A1B1B] flex items-center gap-2 hover:text-[#C5A059] transition-all">
              {showBack ? 'View Front Cover' : 'View Back Cover'}
              <ChevronRight className={`transition-transform duration-500 ${showBack ? 'rotate-180' : ''}`} size={14} />
            </button>
            <button onClick={() => setIsModalOpen(true)} className="sans text-[8px] font-bold tracking-[0.1em] uppercase text-gray-400 flex items-center gap-2 hover:text-[#7A1B1B] transition-colors border-b border-transparent hover:border-[#7A1B1B]">
              <Maximize2 size={10} /> View Full Cover Spread
            </button>
          </div>
        </div>
      </motion.div>
      <motion.div {...fadeInUp} className={`lg:col-span-7 ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
        <div className="space-y-6 md:space-y-10 text-center lg:text-left px-4 sm:px-0">
          <div className="text-left">
            <span className="sans text-[#C5A059] font-black tracking-[0.3em] md:tracking-[0.5em] uppercase text-[10px] md:text-xs mb-4 block uppercase text-left">{book.lang} Edition</span>
            <h3 className="serif text-3xl sm:text-4xl md:text-6xl text-[#7A1B1B] font-medium leading-tight text-left uppercase">{book.title}</h3>
            <p className="sans text-base md:text-xl text-[#7A1B1B] font-bold italic mt-4 md:mt-6 leading-snug text-left">{book.subtitle}</p>
          </div>
          <p className="sans text-sm md:text-lg text-[#2D2D2D] leading-relaxed md:leading-loose opacity-90 italic border-l-0 lg:border-l-2 border-[#C5A059]/30 lg:pl-8 text-left">{book.description}</p>
          <div className="bg-[#7A1B1B]/5 p-4 md:p-6 border-l-4 border-[#7A1B1B] rounded-r-lg shadow-sm text-left">
            <p className="sans text-[11px] md:text-sm font-bold text-[#7A1B1B] leading-relaxed italic text-left uppercase tracking-tight">"{SHARED_CONTENT.CONFESSION_TAG}"</p>
          </div>
          <div className="pt-8 text-left space-y-4">
            <div className="space-y-1 text-left">
              <div className="sans text-sm font-black text-[#7A1B1B] uppercase tracking-widest text-left">Hardcopy</div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-left">
                <span className="sans text-base md:text-lg text-gray-400 line-through font-bold uppercase">MRP {book.mrp}</span>
                <span className="serif text-3xl md:text-4xl font-black text-[#C5A059] uppercase whitespace-nowrap">Available in {book.priceHard}</span>
              </div>
            </div>
            <Link to={`/payment/hardcover?lang=${book.id}`} className="btn-maroon inline-block shadow-lg hover:shadow-2xl transition-all text-[11px] py-5 px-12 uppercase tracking-[0.2em] font-black whitespace-nowrap">Order Now</Link>
          </div>
        </div>
      </motion.div>
      <AnimatePresence>{isModalOpen && <FullCoverModal imageUrl={book.coverImage} onClose={() => setIsModalOpen(false)} />}</AnimatePresence>
    </div>
  );
};

const BookSummary = () => (
  <div className="pt-32 md:pt-48 pb-20 md:pb-32 bg-[#FDFBF7] text-center">
    <div className="max-w-7xl mx-auto px-6 md:px-12 text-center mb-16 md:mb-32">
      <span className="sans text-[#C5A059] font-black tracking-[0.5em] uppercase text-[10px] mb-4 block text-center uppercase">The Literary Collection</span>
      <h2 className="serif text-4xl sm:text-5xl md:text-7xl text-[#7A1B1B] font-medium italic leading-tight text-center uppercase">Complete Editions</h2>
      <div className="h-[1px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent w-full opacity-40 mt-10 mx-auto max-w-xs" />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 space-y-24 md:space-y-52 text-center">
      {BOOKS_DATA.map((book, idx) => <BookEdition key={book.id} book={book} isReversed={idx % 2 !== 0} />)}
    </div>
  </div>
);

const LoginScreen = () => {
  const { adminLogin, sendOtp, loginWithOtp } = useApp();
  const navigate = useNavigate();
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [step, setStep] = useState('input'); // 'input' or 'otp'
  const [formData, setFormData] = useState({ username: '', mobile: '', otp: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async () => {
    if (!formData.mobile) { setError("Enter Mobile Number"); return; }
    setLoading(true);
    const res = await sendOtp(formData.mobile);
    setLoading(false);
    if (res.success) {
      setStep('otp');
      setError('');
    } else setError(res.message);
  };

  // Inside LoginScreen component
  const handleVerifyOtp = async () => {
    if (!formData.otp) {
      setError("Please enter the OTP");
      return;
    }
    setLoading(true);

    // CRITICAL: Must pass BOTH mobile and otp from formData
    const res = await loginWithOtp(formData.mobile, formData.otp);

    setLoading(false);
    if (res.success) {
      navigate('/');
    } else {
      setError(res.message); // This will now correctly show "Incorrect OTP" from PHP
    }
  };

  const handleAdminAuth = async () => {
    if (!formData.username || !formData.mobile) { setError("Enter all details"); return; }
    setLoading(true);
    const res = await adminLogin(formData.username, formData.mobile);
    setLoading(false);
    if (res.success) navigate('/');
    else setError(res.message);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4 md:px-6 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/natural-paper.png')` }} />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#7A1B1B]/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#C5A059]/5 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white p-8 md:p-12 shadow-[0_30px_100px_rgba(0,0,0,0.1)] border border-[#7A1B1B]/5 relative overflow-hidden rounded-2xl"
      >
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#7A1B1B] via-[#C5A059] to-[#7A1B1B]" />

        <div className="flex flex-col items-center mb-10 text-center">
          <motion.div
            key={isAdminMode ? 'admin' : 'reader'}
            initial={{ rotateY: 90 }}
            animate={{ rotateY: 0 }}
            className="w-20 h-20 bg-[#7A1B1B]/5 rounded-2xl flex items-center justify-center mb-6 text-[#7A1B1B] shadow-inner"
          >
            {isAdminMode ? <Shield size={40} className="drop-shadow-sm" /> : <User size={40} className="drop-shadow-sm" />}
          </motion.div>
          <h2 className="serif text-3xl md:text-4xl text-[#7A1B1B] font-medium uppercase tracking-tight leading-tight">
            {isAdminMode ? 'Admin Access' : 'Reader Portal'}
          </h2>
          <p className="sans text-[10px] text-gray-400 font-bold tracking-[0.2em] uppercase mt-2">
            {isAdminMode ? 'Secure Administrative Login' : 'Enter your coordinates to continue'}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-red-50 border-l-4 border-red-500 p-4 mb-6"
            >
              <p className="text-red-700 text-[10px] font-black uppercase tracking-widest">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-5 mb-10">
          <AnimatePresence mode="wait">
            {isAdminMode ? (
              <motion.div
                key="admin-fields"
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                className="space-y-4"
              >
                <div className="relative">
                  <input
                    type="text"
                    placeholder="USERNAME"
                    className="w-full p-5 bg-[#FDFBF7] border border-gray-100 rounded-xl sans text-xs font-bold outline-none focus:border-[#7A1B1B] transition-all pr-12"
                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                  />
                  <Shield size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A1B1B]/20" />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="MOBILE NUMBER"
                    className="w-full p-5 bg-[#FDFBF7] border border-gray-100 rounded-xl sans text-xs font-bold outline-none focus:border-[#7A1B1B] transition-all pr-12"
                    onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                  />
                  <Phone size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A1B1B]/20" />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={step}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                className="space-y-4"
              >
                {step === 'input' ? (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="MOBILE NUMBER"
                      className="w-full p-5 bg-[#FDFBF7] border border-gray-100 rounded-xl sans text-xs font-bold outline-none focus:border-[#7A1B1B] transition-all pr-12"
                      onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                    />
                    <Send size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7A1B1B]/20" />
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="OTP CODE"
                      className="w-full p-5 bg-[#FDFBF7] border border-[#C5A059] rounded-xl sans text-lg font-black outline-none text-center tracking-[0.8em]"
                      maxLength={6}
                      onChange={e => setFormData({ ...formData, otp: e.target.value })}
                    />
                    <div className="text-center mt-4">
                      <p className="sans text-[9px] text-gray-400 font-bold uppercase tracking-widest">Verification code sent to {formData.mobile}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={isAdminMode ? handleAdminAuth : (step === 'input' ? handleSendOtp : handleVerifyOtp)}
          disabled={loading}
          className="w-full py-5 bg-[#7A1B1B] text-white rounded-xl font-black text-[12px] tracking-[0.3em] hover:bg-[#5A1414] transition-all uppercase shadow-[0_15px_30px_rgba(122,27,27,0.2)] flex items-center justify-center gap-3"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              {isAdminMode ? 'AUTHORIZE ACCESS' : (step === 'input' ? 'REQUEST OTP' : 'VERIFY & CONTINUE')}
              <ChevronRight size={18} />
            </>
          )}
        </motion.button>

        <div className="mt-12 pt-8 border-t border-gray-50 flex flex-col items-center gap-4">
          <p
            className="sans text-gray-400 text-[10px] font-bold tracking-[0.2em] uppercase cursor-pointer hover:text-[#7A1B1B] transition-colors flex items-center gap-2"
            onClick={() => { setIsAdminMode(!isAdminMode); setStep('input'); setError(''); }}
          >
            {isAdminMode ? <><User size={12} /> Switch to Reader Portal</> : <><Shield size={12} /> Administrative Access</>}
          </p>
          {step === 'otp' && !isAdminMode && (
            <button
              className="sans text-[#C5A059] text-[10px] font-black tracking-widest uppercase hover:underline"
              onClick={() => { setStep('input'); setError(''); }}
            >
              Correction? Change Number
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const OrdersScreen = () => {
  const { user, isAdmin, orders, refreshOrders } = useApp();
  const navigate = useNavigate();
  useEffect(() => { if (!user) navigate('/login'); else refreshOrders(); }, [user, navigate]);
  return (
    <div className="pt-32 md:pt-48 pb-20 md:pb-32 px-4 md:px-12 max-w-7xl mx-auto min-h-screen text-left">
      <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-16 md:mb-24 border-b border-[#7A1B1B]/10 pb-12 gap-8 text-center md:text-left">
        <div>
          <span className="sans text-[#C5A059] font-black tracking-[0.4em] md:tracking-[0.5em] text-[10px] mb-4 block uppercase italic text-center md:text-left">Logistics Audit Registry</span>
          <h2 className="serif text-4xl sm:text-5xl md:text-7xl text-[#7A1B1B] font-medium leading-none uppercase text-center md:text-left tracking-tight"> {isAdmin ? 'System Logs' : 'My Archive'} </h2>
        </div>
        <div className="bg-[#7A1B1B] text-white px-10 md:px-12 py-6 md:py-8 border-l-8 border-[#C5A059] shadow-2xl relative overflow-hidden group shrink-0 mx-auto md:mx-0">
          <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:rotate-45 transition-transform"><Zap size={80} /></div>
          <span className="sans text-[9px] md:text-[10px] font-black tracking-2em uppercase block mb-2 opacity-70">Entries Found</span>
          <span className="serif text-4xl md:text-6xl font-bold">{orders.length.toString().padStart(2, '0')}</span>
        </div>
      </div>
      <div className="grid gap-6 md:gap-12">
        {orders.length === 0 ? (<div className="py-24 md:py-48 text-center border-2 border-dashed border-gray-200 bg-white"> <ShoppingBag size={60} className="mx-auto mb-10 text-gray-200 animate-pulse" /> <p className="sans text-gray-400 uppercase tracking-[0.5em] text-[10px] md:text-xs font-black uppercase">System Idle • No Activity Found</p> </div>) : (
          orders.map((order, i) => (
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} key={order.id} className="bg-white border-l-[8px] md:border-l-[12px] border-l-[#7A1B1B] border border-gray-100 p-6 md:p-12 flex flex-col md:flex-row justify-between items-center gap-8 shadow-xl transition-all group hover:shadow-2xl">
              <div className="flex-1 space-y-4 text-center md:text-left w-full text-left">
                <div className="flex items-center justify-center md:justify-start gap-4 md:gap-6">
                  <span className="bg-[#7A1B1B]/5 text-[#7A1B1B] px-3 py-1 md:px-4 md:py-2 border border-[#7A1B1B]/10 font-black sans text-[8px] md:text-[10px] tracking-widest uppercase rounded-sm">ORD-{order.id}</span>
                  <span className="sans text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest italic">{order.order_date}</span>
                </div>
                <h3 className="serif text-xl sm:text-2xl md:text-4xl text-[#7A1B1B] font-medium tracking-tight group-hover:text-[#C5A059] transition-colors uppercase text-left">{order.book_name}</h3>
                <div className="flex items-center justify-center md:justify-start gap-2 md:gap-3 text-gray-500 uppercase sans text-[8px] md:text-[9px] font-black tracking-widest">
                  <User size={12} className="text-[#C5A059]" /> {isAdmin ? `Recipient: ${order.fullname} (@${order.username})` : 'Confirmed Delivery Protocol'}
                </div>
              </div>
              <div className="flex items-center gap-8 md:gap-16 w-full md:w-auto justify-between border-t md:border-t-0 border-gray-50 pt-8 md:pt-0">
                <div className="text-right">
                  <span className="sans text-[8px] md:text-[9px] font-black tracking-widest text-gray-400 uppercase block mb-1 md:mb-2 text-right">Asset Value</span>
                  <span className="serif text-3xl md:text-5xl font-bold text-[#7A1B1B]">360/-</span>
                </div>
                <div className="flex items-center gap-3 md:gap-4 bg-[#FDFBF7] text-[#7A1B1B] px-6 md:px-10 py-3 md:py-4 border border-[#7A1B1B]/20 rounded-full shadow-inner shrink-0">
                  <div className="w-2 h-2 rounded-full bg-[#7A1B1B] animate-ping" />
                  <span className="sans text-[9px] md:text-[11px] font-black uppercase tracking-widest">{order.status}</span>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

const PaymentScreen = () => {
  const { user } = useApp();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [address, setAddress] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') || 'english';
  const book = BOOKS_DATA.find(b => b.id === lang) || BOOKS_DATA[2];

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // ✅ NEW FUNCTION: Forces a fresh Razorpay instance to clear old cached mobile numbers
  const loadFreshRazorpay = () => {
    return new Promise((resolve) => {
      // 1. Remove any existing Razorpay scripts to kill the old cache
      const oldScripts = document.querySelectorAll('script[src*="checkout.razorpay.com"]');
      oldScripts.forEach(script => script.remove());
      
      // 2. Delete Razorpay from window memory
      if (window.Razorpay) {
        delete window.Razorpay;
      }

      // 3. Inject a brand new script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = async () => {
    if (!address) { alert("Please enter your Shipping Address."); return; }
    setLoading(true);

    try {
      // Load fresh Razorpay instance BEFORE opening checkout
      const isLoaded = await loadFreshRazorpay();
      if (!isLoaded) throw new Error("Failed to load payment gateway. Please check internet.");

      const numericPrice = parseInt(book.priceHard.replace(/[^0-9]/g, '')) || 1;
      
      const orderRes = await axios.post(`https://vcard.apanel.leewaysofttech.com/DEPRESSION_DIARIES_APIS/orders.php?action=create_razorpay_order`, {
        amount: numericPrice * 100,
        currency: "INR",
        book_id: book.id
      });
      
      const orderData = orderRes.data;
      if (!orderData.id) throw new Error("Order ID not found");

      // ✅ FAILSAFE: Triple check to get the CORRECT current user mobile
      let userMobile = user?.mobile || '';
      if (!userMobile) {
        try {
          const stored = JSON.parse(localStorage.getItem('appUser') || '{}');
          userMobile = stored.mobile || '';
        } catch(e) {}
      }

      const options = {
        key: "rzp_live_TOplWXZ0KAWxtN",
        amount: orderData.amount,
        currency: "INR",
        name: "DR. MANISH KUMAR BANSAL",
        description: `Purchase: ${book.title} (${book.lang} Edition)`,
        order_id: orderData.id,
        handler: async (response) => {
          const verifyRes = await axios.post(`https://vcard.apanel.leewaysofttech.com/DEPRESSION_DIARIES_APIS/orders.php?action=verify_and_place`, {
            user_id: user.id,
            book_name: book.title,
            lang: book.lang,
            amount: numericPrice,
            address: address,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          if (verifyRes.data.status === 'success') setSuccess(true);
          else alert("Payment Verification Failed!");
        },
        prefill: { 
          name: user.fullname || user.username || '', 
          email: "",        // Hides email field completely
          contact: userMobile // Will now strictly show current logged-in user
          
        },
        theme: { color: "#7A1B1B" }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 md:p-8 text-center bg-[#FDFBF7]">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-24 h-24 md:w-32 md:h-32 bg-[#7A1B1B] rounded-full flex items-center justify-center mb-8 md:mb-12 text-white shadow-2xl"><CheckCircle size={48} /></motion.div>
      <h2 className="serif text-4xl md:text-6xl mb-6 text-[#7A1B1B] font-medium italic uppercase tracking-tighter leading-tight text-center">Payment Successful</h2>
      <p className="sans text-lg md:text-xl text-gray-600 mb-10 md:mb-14 max-w-lg mx-auto leading-relaxed italic px-4 text-center">Your payment has been received and verified. Your book will be dispatched shortly. Thank you!</p>
      <Link to="/orders" className="btn-maroon !px-12 md:!px-20 !py-5 md:!py-6 text-[10px] tracking-[0.3em] uppercase font-black">TRACK DISPATCH</Link>
    </div>
  );

  return (
    <div className="pt-32 md:pt-52 pb-20 md:pb-40 px-4 md:px-6 bg-[#FDFBF7] min-h-screen">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto grid lg:grid-cols-12 bg-white shadow-3xl border border-[#7A1B1B]/10 overflow-hidden rounded-sm text-left uppercase">
        <div className="lg:col-span-5 bg-[#7A1B1B] p-8 md:p-16 text-white relative">
          <h2 className="serif text-4xl md:text-7xl mb-10 md:mb-16 font-medium italic leading-tight uppercase text-left">Direct <br /> Checkout</h2>
          <div className="space-y-8 md:space-y-12 relative z-10 text-left">
            <div className="border-b border-white/20 pb-8 md:pb-10">
              <span className="sans text-[9px] md:text-[11px] font-black uppercase tracking-widest opacity-60 mb-4 md:mb-6 block text-left">Requested Resource</span>
              <h3 className="serif text-2xl md:text-3xl font-bold uppercase leading-snug text-left tracking-tight">{book.title} ({book.lang})</h3>
              <span className="sans text-[9px] md:text-[11px] font-black uppercase tracking-widest opacity-80 mt-2 md:mt-4 block uppercase text-left tracking-widest">HARDCOPY</span>
            </div>
            <div>
              <span className="sans text-[9px] md:text-[11px] font-black uppercase tracking-widest opacity-60 mb-2 md:mb-4 block text-left uppercase">Final Amount</span>
              <span className="serif text-5xl md:text-7xl font-bold text-[#C5A059] text-left uppercase">{book.priceHard}</span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 p-8 md:p-16 space-y-8 md:space-y-12 bg-white text-left uppercase">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-10 text-left">
            <div className="space-y-3 md:space-y-4 text-left">
              <label className="sans text-[8px] md:text-[10px] font-black text-[#7A1B1B] uppercase tracking-widest text-left block font-black">Entity Designation</label>
              <input type="text" defaultValue={user?.fullname || user?.username} readOnly className="w-full p-5 border border-gray-100 bg-[#FDFBF7] sans text-xs font-bold outline-none rounded-sm text-left uppercase" />
            </div>
            <div className="space-y-3 md:space-y-4 text-left">
              <label className="sans text-[8px] md:text-[10px] font-black text-[#7A1B1B] uppercase tracking-widest text-left block font-black">Delivery Coordinates</label>
              <input type="text" placeholder="FULL SHIPPING ADDRESS" value={address} onChange={(e) => setAddress(e.target.value)} className="w-full p-4 md:p-5 border border-gray-100 bg-[#FDFBF7] sans text-xs font-bold outline-none focus:border-[#7A1B1B] transition-all rounded-sm text-left uppercase" />
            </div>
          </div>
          <div className="p-6 bg-[#FDFBF7] border border-[#7A1B1B]/10 rounded-sm"> <p className="sans text-[10px] font-bold text-gray-500 uppercase leading-relaxed"> Clicking the button below will open the secure Razorpay payment gateway. You can pay using UPI, Netbanking, or Cards. </p> </div>
          <button onClick={handleRazorpayPayment} disabled={loading} className="w-full py-5 md:py-6 bg-[#7A1B1B] text-white font-black text-[10px] md:text-[13px] tracking-[0.3em] md:tracking-[0.5em] hover:bg-[#5A1414] transition-all uppercase shadow-3xl flex items-center justify-center gap-4 group rounded-sm">
            <span className="relative z-10 uppercase tracking-widest leading-none text-left font-black">{loading ? 'INITIATING SECURE PORTAL...' : 'PAY SECURELY NOW'}</span>
            <Sparkles size={20} className="group-hover:rotate-180 transition-transform shrink-0" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden bg-[#FDFBF7] pt-20 text-left">
    <div className="absolute inset-0 z-0 uppercase"><img src={ASSETS.AUTHOR} alt="Bg" className="w-full h-full object-cover opacity-15 md:opacity-20 uppercase" /><div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/95 lg:via-[#FDFBF7]/80 to-transparent uppercase" /></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full relative z-10 grid lg:grid-cols-2 gap-12 items-center py-12 md:py-24 text-left uppercase">
      <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.2 }} className="text-left">
        <span className="sans text-[#C5A059] font-black tracking-[0.3em] md:tracking-[0.5em] uppercase text-[10px] mb-4 md:mb-6 block italic text-left uppercase">Bridging Science, Spirituality and Governance</span>
        <h1 className="serif text-4xl sm:text-6xl md:text-7xl lg:text-9xl text-[#7A1B1B] leading-tight md:leading-[1.1] mb-6 md:mb-10 font-medium text-left uppercase">Dr. Manish <br /><span className="italic text-[#C5A059]">Kumar Bansal</span></h1>
        <p className="sans text-sm sm:text-base md:text-xl text-[#2D2D2D] max-w-lg mb-10 md:mb-14 leading-relaxed opacity-90 italic font-bold text-left uppercase tracking-tight text-left">IAS Officer & Author of "MY DEPRESSION DIARIES" - {SHARED_CONTENT.MANUAL}</p>
        <div className="flex flex-col sm:flex-row gap-4 md:gap-6 items-stretch sm:items-start max-w-xs sm:max-w-none text-left">
          <Link to="/book" className="btn-maroon shadow-2xl text-center py-5 px-8 md:px-10 text-[10px] md:text-[11px] tracking-[0.2em] whitespace-nowrap uppercase font-black text-left">EXPLORE THE DIARIES</Link>
          <a href="#about" className="px-8 md:px-10 py-5 border-2 border-[#7A1B1B] text-[#7A1B1B] sans text-[10px] md:text-[11px] font-black tracking-[0.2em] hover:bg-[#7A1B1B] hover:text-white transition-all uppercase text-center whitespace-nowrap text-left">LEARN MORE</a>
        </div>
      </motion.div>
      <div className="relative hidden lg:block"> <div className="relative z-10 border-[15px] border-white shadow-2xl rounded-sm overflow-hidden"> <img src={ASSETS.AUTHOR} alt="Author" className="w-full h-auto grayscale hover:grayscale-0 transition-all duration-1000 transform hover:scale-105" /> </div> <div className="absolute -top-10 -right-10 w-full h-full border-[1.5px] border-[#C5A059] -z-0 opacity-40 uppercase" /> </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section id="about" className="py-20 md:py-40 bg-white relative overflow-hidden text-left">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-left uppercase">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center text-left uppercase">
        <motion.div {...fadeInUp} className="order-2 lg:order-1 text-left text-left uppercase">
          <span className="sans text-[#C5A059] font-black tracking-[0.4em] uppercase text-xs mb-4 block text-left uppercase">The Profile</span>
          <h2 className="serif text-4xl md:text-6xl text-[#7A1B1B] mb-8 md:mb-12 leading-tight font-medium text-left uppercase text-left">Bridging <span className="italic">Science, Spirituality</span> & Governance</h2>
          <div className="space-y-6 text-[#2D2D2D] sans text-sm sm:text-base md:text-lg leading-relaxed md:leading-loose opacity-90 italic font-medium text-left text-left uppercase">
            <p>Dr. Manish kumar Bansal is a blend of spiritual seeker and sensitive administrator. A medical doctor (MBBS) who chose the path of public service as an IAS officer in the Gujarat cadre.</p>
            <p>His journey is marked by a brave battle with severe depression during his college years and UPSC days. Instead of getting collapsed, he turned this battle into a learning and explored himself up to the roots with help of medical knowledge and spiritual experiences.</p>
            <p>He wrote his story of depression in the form of a book called “MY DEPRESSION DIARIES” which is not only a blunt confessions about his own weaknesses but also paves a way to overcome them by understanding them but not by suppressing. Book is a blend of science and spirituality in very simple language.</p>
            <p className="italic border-l-4 md:border-l-8 border-[#C5A059] pl-6 md:pl-10 py-5 md:py-8 text-[#7A1B1B] serif text-xl md:text-3xl font-medium bg-[#FDFBF7] shadow-sm leading-relaxed text-left uppercase tracking-tight text-left uppercase leading-snug">"{SHARED_CONTENT.MANUAL}"</p>
          </div>
        </motion.div>
        <div className="order-1 lg:order-2 relative mx-auto lg:mx-0 max-w-md lg:max-w-none w-full text-left uppercase"> <div className="relative z-10 group overflow-hidden shadow-3xl rounded-sm text-left uppercase"> <img src={ASSETS.AUTHOR} alt="Bio" className="w-full h-auto grayscale group-hover:grayscale-0 transition-all duration-1000 transform group-hover:scale-110 uppercase" /> </div> </div>
      </div>
    </div>
  </section>
);

const QuoteSection = () => (
  <section className="py-32 md:py-52 bg-[#7A1B1B] text-center text-white relative overflow-hidden text-center uppercase">
    <div className="absolute inset-0 opacity-5 pointer-events-none uppercase" style={{ backgroundImage: `url('https://www.transparenttextures.com/patterns/natural-paper.png')` }} />
    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center text-center text-center uppercase">
      <Quote className="w-12 h-12 md:w-20 md:h-20 mx-auto mb-10 md:mb-20 text-[#C5A059] opacity-30 text-center text-center uppercase" />
      <h2 className="serif text-3xl sm:text-5xl md:text-7xl italic leading-snug md:leading-[1.3] mb-10 md:mb-16 font-medium px-4 md:px-0 text-center uppercase tracking-tighter uppercase text-center text-center uppercase leading-tight">"Success is not final, failure is not fatal: it is the <span className="text-[#C5A059]">courage to continue</span> that counts."</h2>
      <p className="sans text-[10px] md:text-sm font-black tracking-[0.6em] md:tracking-[1em] uppercase text-[#C5A059] text-center uppercase text-center text-center uppercase">Dr. Manish kumar Bansal</p>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#1A1A1A] pt-20 md:pt-32 pb-12 md:pb-16 px-6 md:px-12 text-white text-center border-t border-white/5 uppercase">
    <div className="max-w-7xl mx-auto">
      <h4 className="serif text-2xl md:text-4xl mb-6 md:mb-10 font-medium text-center uppercase leading-none tracking-tighter">
        Dr. Manish kumar <span className="text-[#C5A059]">Bansal</span>
      </h4>
      <div className="flex justify-center gap-8 md:gap-12 mb-16 md:mb-24 text-gray-500">
        <Mail size={24} className="hover:text-[#C5A059] cursor-pointer transition-colors" />
        <Twitter size={24} className="hover:text-[#C5A059] cursor-pointer transition-colors" />
        <Linkedin size={24} className="hover:text-[#C5A059] cursor-pointer transition-colors" />
      </div>
      <div className="pt-10 md:pt-14 border-t border-white/5 space-y-12">
        <p className="sans text-[9px] md:text-[11px] tracking-[0.4em] uppercase text-gray-700 font-bold">
          © {new Date().getFullYear()} ALL RIGHTS RESERVED.
        </p>

        <div className="py-12 flex justify-center">
          <motion.div
            whileHover={{ y: -5 }}
            className="group relative"
          >
            {/* Main Glossy Container */}
            <div className="relative overflow-hidden px-8 md:px-16 py-8 md:py-12 rounded-3xl bg-white/[0.03] border border-white/10 backdrop-blur-2xl shadow-2xl transition-all duration-700 hover:border-[#C5A059]/40 hover:shadow-[#C5A059]/20">

              {/* Shine Animation */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.05] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

              <div className="flex flex-col md:flex-row items-center gap-5 md:gap-10">
                <div className="flex flex-col items-center md:items-end space-y-1">
                  <span className="sans text-[8px] tracking-[1em] text-[#C5A059] font-black opacity-60 uppercase">PROUDLY</span>
                  <span className="sans text-[12px] md:text-[15px] font-black tracking-[0.4em] bg-gradient-to-r from-[#C5A059] via-[#FFF] to-[#C5A059] bg-clip-text text-transparent drop-shadow-[0_0_8px_rgba(197,160,89,0.4)] uppercase">
                    MARKETING BY
                  </span>
                </div>

                {/* Elegant Divider */}
                <div className="hidden md:block h-14 w-[1px] bg-gradient-to-b from-transparent via-[#C5A059]/40 to-transparent mx-2" />

                {/* Interactive Link */}
                <a
                  href="https://leewaysoftech.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-5 group/link"
                >
                  <div className="flex flex-col items-center md:items-start">
                    <span className="sans text-[22px] md:text-[34px] font-black tracking-[0.25em] bg-gradient-to-r from-[#C5A059] via-[#F3E5AB] to-[#C5A059] bg-clip-text text-transparent drop-shadow-[0_5px_20px_rgba(197,160,89,0.5)] group-hover/link:scale-105 transition-all duration-500 uppercase">
                      LEEWAY SOFTECH
                    </span>
                    <div className="h-[2px] w-0 bg-gradient-to-r from-[#C5A059] to-transparent group-hover/link:w-full transition-all duration-700 rounded-full mt-2" />
                  </div>

                  {/* Decorative Link Circle */}
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-[#C5A059] to-[#D4AF37] transform group-hover/link:rotate-[360deg] transition-all duration-1000 shadow-[0_0_30px_rgba(197,160,89,0.3)] hover:scale-110">
                    <ExternalLink size={26} className="text-[#1A1A1A]" />
                  </div>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </footer>
);

const AppContent = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  return (
    <div className="min-h-screen antialiased selection:bg-[#7A1B1B]/20 selection:text-[#7A1B1B] text-left overflow-x-hidden uppercase">
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-[#C5A059] z-[150] origin-left shadow-lg" style={{ scaleX }} />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<><Hero /><AboutSection /><QuoteSection /></>} />
          <Route path="/book" element={<BookSummary />} />
          <Route path="/orders" element={<OrdersScreen />} />
          <Route path="/payment/:type" element={<PaymentScreen />} />
          <Route path="/login" element={<LoginScreen />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() { return (<AppProvider><Router><AppContent /></Router></AppProvider>); }

export default App;